import React, { useCallback, useEffect, useRef, useState } from 'react'
import whatsappApi from './whatsappApi'
import myToast from '../../Utils/MyToast'

const WA_GREEN = '#25D366'
const WA_GREEN_DARK = '#128C7E'

// Created By Cursor
function formatPlanValidity(value) {
  const v = String(value || 'UNLIMITED').toUpperCase()
  if (v === 'MONTHLY') return 'Monthly'
  if (v === 'UNLIMITED') return 'Unlimited'
  return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Created By Cursor
const WhatsAppSubscriptionPanel = ({ subscription: initialSub, wallet, onChanged }) => {
  const [subscription, setSubscription] = useState(initialSub)
  const [plans, setPlans] = useState([])
  const [overageRates, setOverageRates] = useState([])
  const [loading, setLoading] = useState(true)
  const [assigning, setAssigning] = useState(false)
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged

  // Created By Cursor
  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await whatsappApi.getSubscription()
      if (res.api_status === 'OK' && res.data) {
        setSubscription(res.data.subscription || null)
        setPlans(res.data.plans || [])
        setOverageRates(res.data.overage_rates || [])
        if (onChangedRef.current) onChangedRef.current(res.data)
      } else {
        myToast.notify_error(res.api_remark || 'Failed to load subscription')
      }
    } catch (e) {
      myToast.notify_error('Failed to load subscription')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    setSubscription(initialSub)
  }, [initialSub])

  const assignPlan = async (planId) => {
    if (!window.confirm('Change subscription plan? Usage for the new cycle will reset to 0.')) {
      return
    }
    setAssigning(true)
    try {
      const res = await whatsappApi.assignSubscription(planId)
      if (res.api_status === 'OK') {
        myToast.notify_success('Plan updated')
        await refresh()
      } else {
        myToast.notify_error(res.api_remark || 'Could not update plan')
      }
    } catch (e) {
      myToast.notify_error('Could not update plan')
    } finally {
      setAssigning(false)
    }
  }

  if (loading && !subscription) {
    return <div className="text-muted">Loading subscription…</div>
  }

  const used = Number(subscription?.messages_used_this_cycle || 0)
  const planType = String(subscription?.plan_type || 'MONTHLY').toUpperCase()
  const includedRaw = subscription?.included_messages
  const included =
    includedRaw === null || includedRaw === undefined || includedRaw === ''
      ? null
      : Number(includedRaw)
  const remaining =
    subscription?.messages_remaining === null || subscription?.messages_remaining === undefined
      ? null
      : Number(subscription.messages_remaining)
  const pct =
    subscription?.usage_percent === null || subscription?.usage_percent === undefined
      ? null
      : Number(subscription.usage_percent)
  const inOverage = !!subscription?.in_overage
  const balance = Number(wallet?.balance || 0)

  return (
    <div style={{ padding: '4px 0' }}>
      <p style={{ color: '#4b5563', maxWidth: 720, marginBottom: 16 }}>
        {planType === 'USAGE_BASED'
          ? 'Usage-based plan — each message is billed from your EduOK overage wallet. Meta still bills your school card for delivery.'
          : 'Monthly plan includes a message allowance. Wallet is used only after the allowance is exhausted. Meta bills your school card for delivery.'}
      </p>

      {subscription ? (
        <div
          style={{
            border: '1px solid #bbf7d0',
            background: '#f0fdf4',
            borderRadius: 12,
            padding: 18,
            marginBottom: 18,
            maxWidth: 720,
          }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: WA_GREEN_DARK, textTransform: 'uppercase' }}>
                Current plan
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
                {subscription.plan_name || '—'}
              </div>
              <div style={{ color: '#475569', fontSize: 13 }}>
                ₹{Number(subscription.plan_value ?? subscription.monthly_fee ?? 0).toFixed(0)}
                {` · Validity ${formatPlanValidity(subscription.plan_validity)}`}
                {included === null
                  ? ' · usage based'
                  : ` · ${included.toLocaleString('en-IN')} messages included`}
                {subscription.plan_id ? ` · ${subscription.plan_id}` : ''}
              </div>
            </div>
            <span
              style={{
                background: inOverage ? '#fef3c7' : '#dcfce7',
                color: inOverage ? '#92400e' : '#166534',
                fontWeight: 700,
                fontSize: 12,
                padding: '6px 10px',
                borderRadius: 999,
              }}
            >
              {inOverage ? 'In overage' : 'Within allowance'}
            </span>
          </div>

          {included !== null ? (
            <div style={{ marginTop: 16 }}>
              <div className="d-flex justify-content-between" style={{ fontSize: 13, marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: '#334155' }}>
                  {used.toLocaleString('en-IN')} / {included.toLocaleString('en-IN')} used
                </span>
                <span style={{ color: '#64748b' }}>
                  {(remaining ?? 0).toLocaleString('en-IN')} remaining · resets{' '}
                  {subscription.cycle_end_date || '—'}
                </span>
              </div>
              <div
                style={{
                  height: 10,
                  borderRadius: 999,
                  background: '#e2e8f0',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, pct || 0)}%`,
                    height: '100%',
                    background: inOverage
                      ? 'linear-gradient(90deg, #f59e0b, #ef4444)'
                      : `linear-gradient(90deg, ${WA_GREEN_DARK}, ${WA_GREEN})`,
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 12, fontSize: 13, color: '#64748b', fontWeight: 600 }}>
              Used this cycle: {used.toLocaleString('en-IN')} · resets{' '}
              {subscription.cycle_end_date || '—'}
            </div>
          )}

          {inOverage && (
            <div
              style={{
                marginTop: 14,
                padding: '10px 12px',
                borderRadius: 8,
                background: balance < 50 ? '#fef2f2' : '#fffbeb',
                color: balance < 50 ? '#991b1b' : '#92400e',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {included === null ? 'Usage-based billing.' : 'Allowance exhausted.'} Overage wallet: ₹
              {balance.toFixed(2)}
              {balance < 50 ? ' — recharge recommended before more sends.' : ''}
            </div>
          )}
        </div>
      ) : (
        <div className="alert alert-warning">No subscription assigned yet.</div>
      )}

      <h6 style={{ fontWeight: 800, marginBottom: 10 }}>Available plans</h6>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          marginBottom: 20,
          maxWidth: 900,
        }}
      >
        {plans.map((p) => {
          const code = String(p.plan_id || p.id)
          const active = String(subscription?.plan_id || '') === code
          const included = p.included_messages
          const planType = String(p.plan_type || 'MONTHLY').toUpperCase()
          return (
            <div
              key={code}
              style={{
                border: active ? `2px solid ${WA_GREEN}` : '1px solid #e2e8f0',
                background: active ? 'rgba(37,211,102,0.08)' : '#fff',
                borderRadius: 12,
                padding: 14,
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 16 }}>{p.plan_name}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginTop: 2 }}>
                {planType === 'USAGE_BASED' ? 'Usage based' : 'Monthly'} · {code}
              </div>
              <div style={{ color: WA_GREEN_DARK, fontWeight: 700, marginTop: 4 }}>
                ₹{Number(p.plan_value ?? p.monthly_fee ?? 0).toFixed(0)}
              </div>
              <div style={{ fontSize: 12, color: '#475569', fontWeight: 700, marginTop: 4 }}>
                Validity: {formatPlanValidity(p.plan_validity)}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                {planType === 'USAGE_BASED' || included === null || included === ''
                  ? 'Pay per message (wallet)'
                  : `${Number(included).toLocaleString('en-IN')} messages / cycle`}
              </div>
              {p.overage_price_per_message != null && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  Overage ₹{Number(p.overage_price_per_message).toFixed(4)}/msg
                </div>
              )}
              <button
                type="button"
                className="btn btn-sm mt-3"
                disabled={assigning || active}
                onClick={() => assignPlan(code)}
                style={{
                  background: active ? '#94a3b8' : WA_GREEN,
                  color: '#fff',
                  fontWeight: 700,
                  borderRadius: 8,
                }}
              >
                {active ? 'Current' : 'Select plan'}
              </button>
            </div>
          )
        })}
      </div>

      <h6 style={{ fontWeight: 800, marginBottom: 8 }}>Overage rates (after allowance)</h6>
      <div className="table-responsive" style={{ maxWidth: 560 }}>
        <table className="table table-sm table-bordered mb-0">
          <thead className="thead-light">
            <tr>
              <th>Category</th>
              <th>Overage / msg</th>
            </tr>
          </thead>
          <tbody>
            {overageRates.map((r) => (
              <tr key={r.id}>
                <td style={{ textTransform: 'capitalize' }}>{r.conversation_category}</td>
                <td>
                  ₹
                  {Number(r.overage_price_per_message ?? r.price_per_message).toFixed(4)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default WhatsAppSubscriptionPanel
